import React, { FC, useState } from "react";
import { Card, Form, Button, Input } from "antd";
import styled from "styled-components";
import { Store } from "antd/lib/form/interface";
import { useResetPasswordMutation } from "../../queries/auto-generated/hooks";
import _ from "lodash";
import { passwordMin, passwordMax } from "./register";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import user from "../../ducks/user";
import { RootState } from "../../ducks";
import queryString from "query-string";
import { ACCOUNT_PAGE_PATH, PASSWORD_RESET_PAGE_PATH } from "../../router";
import { ViewTypeUpdater } from ".";

const PasswordReset: FC<{ setType: ViewTypeUpdater }> = ({ setType }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [resetPasswordMutation] = useResetPasswordMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedAtLeastOnce, setHasSubmittedAtLeastOnce] = useState<
    boolean
  >(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const history = useHistory();
  const { passwordResetCode: hiddenCode } = useSelector(
    (state: RootState) => state.user
  );

  const search = history.location.search;

  if (search) {
    const { code } = queryString.parse(search);
    dispatch(user.actions.setPasswordResetCode({ code: code as string }));
    history.replace(PASSWORD_RESET_PAGE_PATH); // Remove code from url
  }

  const validateTrigger = hasSubmittedAtLeastOnce
    ? ["onChange", "onSubmit"]
    : "onSubmit";

  const handleButtonClick = () => {
    setIsSubmitting(true);
    setErrorMessages([]);
    setHasSubmittedAtLeastOnce(true);
    form.submit();
  };

  const handleFinish = (values: Store) => {
    console.log({ values });
    const { password, passwordConfirmation, code } = values;
    resetPasswordMutation({
      variables: {
        password,
        passwordConfirmation,
        code,
      },
    })
      .then(({ data }) => {
        const jwt = data?.resetPassword?.jwt ?? "";

        dispatch(user.actions.setPasswordResetCode({ code: undefined }));

        if (!jwt) {
          throw new Error("Issue logging in");
        } else {
          setType(undefined);
          history.push(ACCOUNT_PAGE_PATH);
        }
      })
      .catch((e) => {
        console.error(JSON.stringify(e));

        const errorId = _.get(
          e,
          "graphQLErrors[0].extensions.exception.data.message[0].messages[0].id"
        );
        if (errorId === "Auth.form.error.code.provide") {
          setErrorMessages(["Password reset link has expired"]);
        } else {
          setErrorMessages(["There was a problem resetting the password"]);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleFinishedFailed = () => {
    setIsSubmitting(false);
  };

  const ErrorMessages: FC = () => {
    if (!errorMessages || errorMessages.length === 0) {
      return null;
    }

    return (
      <StyledList>
        {errorMessages.map((message) => (
          <li>{message}</li>
        ))}
      </StyledList>
    );
  };

  const primaryAction = (
    <Button
      size="large"
      type="primary"
      shape="round"
      block
      onClick={handleButtonClick}
      loading={isSubmitting}
    >
      Reset password
    </Button>
  );

  return (
    <Card title="Password reset">
      <Form
        form={form}
        name="password_reset"
        initialValues={{ code: hiddenCode }}
        scrollToFirstError
        colon={false}
        hideRequiredMark
        onFinish={handleFinish}
        onFinishFailed={handleFinishedFailed}
        validateTrigger={validateTrigger}
      >
        <StyledItem
          name="password"
          label="New password"
          rules={[
            {
              required: true,
              message: "Please input your password",
            },
            {
              message: `Password must be between ${passwordMin} and ${passwordMax} characters long`,
              min: passwordMin,
              max: passwordMax,
            },
          ]}
        >
          <Input.Password placeholder="Password" />
        </StyledItem>

        <StyledItem
          name="passwordConfirmation"
          label="Confirm password"
          dependencies={["password"]}
          rules={[
            {
              required: true,
              message: "Please repeat your password",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject("Passwords do not match");
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm password" />
        </StyledItem>
        <Form.Item
          name="code"
          hidden
          rules={[
            () => ({
              validator(_, value) {
                if (!!value) {
                  return Promise.resolve();
                }
                setErrorMessages([
                  "There was an issue. Please click on the reset link from the email again",
                ]);
                return Promise.reject();
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>
        <ErrorMessages />
        {primaryAction}
      </Form>
    </Card>
  );
};

const StyledItem = styled(Form.Item)`
  display: block;
`;

const StyledList = styled.ul`
  list-style: none;
  color: #a61d24;
  padding-left: 0;
`;

export default PasswordReset;
